import { FlagRule, Money } from "@/components/common";

const Bi = ({ en, ar }) => (<span>{en} <span className="font-arabic text-slate-400">/ {ar}</span></span>);

export const InvoiceDocument = ({ inv, co, kind = "invoice" }) => {
  const titles = { invoice: ["Tax Invoice", "فاتورة ضريبية"], quote: ["Quotation", "عرض سعر"], credit_note: ["Tax Credit Note", "إشعار دائن ضريبي"] };
  const [titleEn, titleAr] = titles[kind];
  const cur = inv.currency || "AED";
  const fx = cur !== "AED";
  const sub = fx ? inv.fx_subtotal : inv.subtotal, vat = fx ? inv.fx_vat_total : inv.vat_total, tot = fx ? inv.fx_total : inv.total;
  return (
  <div dir="ltr" data-testid="invoice-document" className="print-sheet mx-auto max-w-[820px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <FlagRule />
    <div className="p-8 sm:p-12">
      <div className="flex flex-wrap justify-between gap-6">
        <div>
          <div className="font-display text-2xl font-extrabold text-slate-900">{co.company_name || co.name}</div>
          <div className="mt-1 whitespace-pre-line text-sm text-slate-500">{co.address}</div>
          {co.phone && <div className="text-sm text-slate-500">{co.phone}</div>}
          <div className="mt-2 font-mono text-sm text-slate-700">TRN: {co.trn || "—"}</div>
        </div>
        <div className="text-right">
          <div className={`font-display text-3xl font-extrabold uppercase tracking-tight ${kind === "credit_note" ? "text-[#CE1126]" : "text-[#007A3D]"}`}>{titleEn}</div>
          <div className="font-arabic text-xl text-slate-400">{titleAr}</div>
          <div className="mt-3 font-mono text-lg font-semibold" data-testid="invoice-number">{inv.number || (inv.status === "draft" ? <span className="rounded bg-slate-100 px-2 py-0.5 text-sm uppercase tracking-widest text-slate-500">Draft / مسودة</span> : "")}</div>
          {inv.invoice_number && <div className="text-xs text-slate-500">Ref. invoice {inv.invoice_number}</div>}
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-6 border-y border-slate-200 py-6 text-sm">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400"><Bi en="Bill to" ar="فاتورة إلى" /></div>
          <div className="mt-2 font-semibold text-slate-900">{inv.client_name}</div>
          <div className="text-slate-500">{inv.client_address}</div>
          {inv.client_trn && <div className="mt-1 font-mono text-slate-700">TRN: {inv.client_trn}</div>}
        </div>
        <div className="space-y-1 text-right">
          <div><span className="text-slate-400">Issue date / تاريخ الإصدار:</span> <span className="font-mono">{inv.issue_date}</span></div>
          {inv.due_date && <div><span className="text-slate-400">{kind === "quote" ? "Valid until / صالح حتى" : "Due date / تاريخ الاستحقاق"}:</span> <span className="font-mono">{inv.due_date}</span></div>}
          <div><span className="text-slate-400">Currency:</span> <span data-testid="invoice-doc-currency">{cur}</span>{fx && <span className="ms-2 font-mono text-xs text-slate-400">1 {cur} = {inv.fx_rate} AED</span>}</div>
        </div>
      </div>
      <table className="mt-6 w-full text-sm">
        <thead><tr className="border-b-2 border-slate-900 text-left text-[11px] uppercase tracking-wider text-slate-500">
          <th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit price</th><th className="py-2 text-right">VAT</th><th className="py-2 text-right">VAT amt</th><th className="py-2 text-right">Net</th>
        </tr></thead>
        <tbody>
          {inv.items.map((it, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-3">{it.description}</td>
              <td className="py-3 text-right font-mono">{it.quantity}</td>
              <td className="py-3 text-right"><Money value={it.unit_price} cur={false} /></td>
              <td className="py-3 text-right font-mono text-xs">{it.vat_type === "standard" ? "5%" : it.vat_type === "zero" ? "0%" : "Exempt"}</td>
              <td className="py-3 text-right"><Money value={it.vat} cur={false} /></td>
              <td className="py-3 text-right"><Money value={it.net} cur={false} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-6 ml-auto w-full max-w-xs space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Subtotal (excl. VAT)</span><Money value={sub} cur={cur} /></div>
        <div className="flex justify-between"><span className="text-slate-500">VAT 5% / ضريبة القيمة المضافة</span><Money value={vat} cur={cur} testid="invoice-view-vat" /></div>
        <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-base font-bold"><span>{kind === "credit_note" ? "Total credited / إجمالي الدائن" : "Total due / المجموع"}</span><Money value={tot} cur={cur} testid="invoice-view-total" className={kind === "credit_note" ? "text-[#CE1126]" : "text-[#007A3D]"} /></div>
        {fx && (
          <div data-testid="invoice-doc-aed-block" className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
            <div className="mb-1 font-semibold uppercase tracking-wider text-slate-500">AED equivalent / المعادل بالدرهم</div>
            <div className="flex justify-between"><span>Subtotal</span><Money value={inv.subtotal} /></div>
            <div className="flex justify-between"><span>VAT 5%</span><Money value={inv.vat_total} testid="invoice-view-vat-aed" /></div>
            <div className="flex justify-between font-bold"><span>Total</span><Money value={inv.total} testid="invoice-view-total-aed" /></div>
            <div className="mt-1 text-[10px] text-slate-400">Exchange rate 1 {cur} = {inv.fx_rate} AED (FTA Art. 69 — VAT amount shown in AED)</div>
          </div>
        )}
      </div>
      {inv.reason && <div className="mt-6 text-sm text-slate-600"><span className="font-semibold">Reason / السبب:</span> {inv.reason}</div>}
      {inv.notes && <div className="mt-10 whitespace-pre-line rounded-lg bg-slate-50 p-4 text-sm text-slate-600">{inv.notes}</div>}
    </div>
  </div>
  );
};
